import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { API_URL, CONFIG } from "../helpers/constants";

// Create Context
const SportsbookContext = createContext();

// Custom Hook
export const useSportsbook = () => useContext(SportsbookContext);

// Provider Component
export const SportsbookProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sportsbookUrl, setSportsbookUrl] = useState("");
  const [sportsbookAction, setSportsbookAction] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const scriptLoaded = useRef(false);
  const initializedAsGuest = useRef(false);
  const initializedForUser = useRef(null);
  const sportsbookRef = useRef(null); // Iframe reference

  const MAX_RETRIES = 10;
  const RETRY_DELAY = 500;

  // Login User
  const userLogin = async (userId, currency, type) => {
    try {
      if (!userId) return "guest";

      const response = await fetch(API_URL + "/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, currency, type }),
      });

      const rawText = await response.text();
      const data = JSON.parse(rawText);

      return data.success ? data.token : "guest";
    } catch (error) {
      console.error("Error during user login:", error);
      return "guest";
    }
  };

  // Fetch CMS Token
  const fetchCMSToken = async () => {
    try {
      const response = await fetch(API_URL + "/get-cms-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      const rawText = await response.text();
      const data = JSON.parse(rawText);
      return data.success ? data.token : null;
    } catch (error) {
      console.error("Error fetching CMS token:", error);
      return null;
    }
  };

  const sendMessageToSportsbook = (message, attempt = 0) => {
    return new Promise((resolve, reject) => {
      if (!sportsbookRef.current || !sportsbookRef.current.contentWindow) {
        if (attempt < MAX_RETRIES) {
          console.warn(`⚠️ Iframe not available. Retrying in ${RETRY_DELAY}ms... (Attempt ${attempt + 1})`, message);
          setTimeout(
            () =>
              sendMessageToSportsbook(message, attempt + 1)
                .then(resolve)
                .catch(reject),
            RETRY_DELAY
          );
        } else {
          console.error("❌ Max retries reached. Could not send message:", message);
          // setLoading(false);
          setError("Unable to load sportsbook please retry.");
        }
        return;
      }

      console.log("✅ Sending message to Sportsbook:", message);
      sportsbookRef.current.contentWindow.postMessage(JSON.stringify(message), "*");
      setSportsbookAction(message);

      resolve("Message sent successfully");
    });
  };

  // Load sportsbook script ONCE
  useEffect(() => {
    if (scriptLoaded.current) return;

    const script = document.createElement("script");
    script.id = "sportsbook-script";
    script.src = "https://sportsbook.cmswager.com/js/sportsbook.js";
    script.async = true;
    script.onload = () => console.log("Sportsbook script loaded.");

    document.body.appendChild(script);
    scriptLoaded.current = true;
  }, [sportsbookUrl]);

  const initializeSportsbook = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const platform = window.innerWidth <= 640 ? "mobile" : window.innerWidth <= 1024 ? "tablet" : "desktop";
      let defaultPage = "sport";
      if (location.pathname === "/sportsbook/sports" || sportsbookAction.changePage === "sport") {
        defaultPage = "sport";
        navigate("/sportsbook/sports");
      } else if (location.pathname === "/sportsbook/live" || sportsbookAction.changePage === "live") {
        defaultPage = "live";
        navigate("/sportsbook/live");
      } else if (location.pathname.startsWith("/sportsbook/bet-history")) {
        defaultPage = "bet-history";
      }

      let cmsToken;
      let userToken = "guest";

      if (user) {
        // 🔥 Remove early return so it always initializes when navigating back
        initializedForUser.current = user?.c_id;

        cmsToken = await fetchCMSToken();
        if (!cmsToken) throw new Error("CMS Token not available.");
        userToken = (await userLogin(user?.c_id, "TND", "player")) || "guest";
      } else {
        initializedAsGuest.current = true; // Remove check to allow reinitialization
      }

      // Ensure CMS token is set correctly
      const integrationToken = CONFIG.CLIENT_KEY || cmsToken;

      const newUrl = `${CONFIG.CMS_BASE_URL}?language=${CONFIG.DEFAULT_LANG}&token=${userToken}&integration=${integrationToken}&platform=${platform}&defaultpage=${defaultPage}`;

      setSportsbookUrl(newUrl);
    } catch (error) {
      console.error("❌ Error initializing Sportsbook:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [location.pathname, user, sportsbookAction]);

  useEffect(() => {
    if (location.pathname.startsWith("/sportsbook")) {
      console.log("🔄 Re-initializing Sportsbook...");
      initializeSportsbook();
    }
  }, [location.pathname]);

  return (
    <SportsbookContext.Provider
      value={{ sendMessageToSportsbook, sportsbookRef, sportsbookAction, setSportsbookAction, sportsbookUrl, loading, error }}
    >
      {children}
    </SportsbookContext.Provider>
  );
};
